"use client";

import React, { useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Send,
  CheckCircle2,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { submitContactInquiry } from "@/lib/contact-api";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);
const IKFooter = dynamic(() => import("@/components/IKFooter").then((m) => m.IKFooter), { ssr: true });

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (name: string, value: string) => {
    let error = "";
    if (name === "name") {
      if (!value.trim()) {
        error = "Full Name is required";
      } else if (value.trim().length < 2) {
        error = "Name must be at least 2 characters long";
      }
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        error = "Email address is required";
      } else if (!emailRegex.test(value)) {
        error = "Please enter a valid email address";
      }
    } else if (name === "phone") {
      const phoneRegex = /^\+?[\d\s-]{10,15}$/;
      if (!value.trim()) {
        error = "Phone number is required";
      } else if (!phoneRegex.test(value.replace(/\s+/g, ""))) {
        error = "Please enter a valid phone number (10 to 15 digits)";
      }
    } else if (name === "subject") {
      if (!value.trim()) {
        error = "Subject is required";
      }
    } else if (name === "message") {
      if (!value.trim()) {
        error = "Message is required";
      } else if (value.trim().length < 10) {
        error = "Message must be at least 10 characters long";
      }
    }
    return error;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));

    const error = validateField(id, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[id] = error;
      } else {
        delete newErrors[id];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const response = await submitContactInquiry({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      });

      if (response.success) {
        setSubmitted(true);
      } else {
        setApiError(response.message || "Failed to submit message. Please try again.");
      }
    } catch (err: any) {
      setApiError(err?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    });
    setErrors({});
    setApiError(null);
    setSubmitted(false);
  };

  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumbs */}
          <nav className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-8 font-medium" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0" aria-hidden />
            <span className="text-[#131222] dark:text-white font-semibold">
              Contact Us
            </span>
          </nav>

          {/* Page Header */}
          <div className="mb-12 text-center max-w-3xl mx-auto animate-fadeIn">
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-4">
              Get in <span className="text-[#FFB51D]">Touch</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-base md:text-lg font-medium tracking-wide">
              We're here to help you drive your journey, your way.
            </p>
            <div className="h-1 w-20 bg-[#FFB51D] mx-auto rounded-full mt-4" />
          </div>

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16 animate-fadeIn">
            {/* Left Side: Contact Information Cards (lg:col-span-5) */}
            <div className="lg:col-span-5 space-y-6">
              <h2 className="text-xl md:text-2xl font-bold text-[#131222] dark:text-white mb-2">
                Contact Information
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base leading-relaxed mb-6">
                Have questions about renting? Reach out to our team through any of the channels below.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                {/* Card 1: Phone Support */}
                <a
                  href="tel:+919809548899"
                  className="group rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-5 bg-white dark:bg-neutral-900/30 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
                  title="Call +91 9809548899"
                >
                  <div className="p-3 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                    <Phone className="w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#131222] dark:text-white text-sm md:text-base mb-1">
                      Phone Support
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-xs md:text-sm font-medium">
                      +91 9809548899
                    </p>
                  </div>
                </a>

                {/* Card 2: Email Support */}
                <a
                  href="mailto:ikbs8899@gmail.com"
                  className="group rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-5 bg-white dark:bg-neutral-900/30 hover:shadow-lg transition-all duration-300 flex items-start gap-4"
                  title="Send email to ikbs8899@gmail.com"
                >
                  <div className="p-3 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                    <Mail className="w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#131222] dark:text-white text-sm md:text-base mb-1">
                      Email Address
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-xs md:text-sm font-medium break-all">
                      ikbs8899@gmail.com
                    </p>
                  </div>
                </a>

                {/* Card 3: Address */}
                <a
                  href="https://www.google.com/maps/search/?api=1&query=G-725%2FD%2C+Main+Road%2C+Sawantwadi%2C+Opp.+Renuka+Palace%2C+Sawantwadi%2C+Sindhudurg%2C+Maharashtra+%E2%80%93+416510"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-5 bg-white dark:bg-neutral-900/30 hover:shadow-lg transition-all duration-300 flex items-start gap-4 sm:col-span-2 lg:col-span-1"
                  title="Open location in Google Maps"
                >
                  <div className="p-3 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-5 h-5 shrink-0" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#131222] dark:text-white text-sm md:text-base mb-1">
                      Office Address
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-xs md:text-sm font-medium leading-relaxed">
                      G-725/D, Main Road, Sawantwadi, Opp. Renuka Palace, Sawantwadi, Sindhudurg, Maharashtra – 416510
                    </p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Side: Interactive Contact Form Card (lg:col-span-7) */}
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 sm:p-8 bg-white dark:bg-neutral-900/30 shadow-md">
                {submitted ? (
                  /* Success Screen */
                  <div className="text-center py-8 animate-fadeIn flex flex-col items-center">
                    <div className="p-4 bg-[#FFB51D]/10 rounded-full text-[#FFB51D] mb-4">
                      <CheckCircle2 className="w-16 h-16" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#131222] dark:text-white mb-2">
                      Thank You!
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-md mx-auto mb-6 leading-relaxed">
                      Your message has been sent successfully. Our support team will get in touch with you shortly.
                    </p>
                    <Button
                      onClick={handleReset}
                      className="h-11 px-6 bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] text-[#131222] font-bold rounded-xl transition-all duration-300"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  /* Form Fields Input Screen */
                  <form onSubmit={handleSubmit} noValidate className="space-y-5 animate-fadeIn">
                    <h2 className="text-xl md:text-2xl font-bold text-[#131222] dark:text-white mb-2 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-[#FFB51D]" /> Send Us a Message
                    </h2>

                    {apiError && (
                      <div className="p-3.5 bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs md:text-sm rounded-xl font-medium animate-fadeIn">
                        {apiError}
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Name */}
                      <div className="space-y-1.5">
                        <label
                          htmlFor="name"
                          className="text-xs font-semibold text-neutral-600 dark:text-neutral-400"
                        >
                          Full Name *
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className={`w-full h-11 px-4 rounded-xl border bg-transparent text-[#131222] dark:text-white text-sm focus:outline-none focus:ring-2 transition-all ${
                            errors.name
                              ? "border-red-500 focus:ring-red-500"
                              : "border-neutral-300 dark:border-neutral-700 focus:ring-[#FFB51D] focus:border-transparent"
                          }`}
                        />
                        {errors.name && (
                          <span className="text-xs text-red-500 mt-1 block font-medium animate-fadeIn">
                            {errors.name}
                          </span>
                        )}
                      </div>

                      {/* Email */}
                      <div className="space-y-1.5">
                        <label
                          htmlFor="email"
                          className="text-xs font-semibold text-neutral-600 dark:text-neutral-400"
                        >
                          Email Address *
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="johndoe@example.com"
                          className={`w-full h-11 px-4 rounded-xl border bg-transparent text-[#131222] dark:text-white text-sm focus:outline-none focus:ring-2 transition-all ${
                            errors.email
                              ? "border-red-500 focus:ring-red-500"
                              : "border-neutral-300 dark:border-neutral-700 focus:ring-[#FFB51D] focus:border-transparent"
                          }`}
                        />
                        {errors.email && (
                          <span className="text-xs text-red-500 mt-1 block font-medium animate-fadeIn">
                            {errors.email}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Phone */}
                      <div className="space-y-1.5">
                        <label
                          htmlFor="phone"
                          className="text-xs font-semibold text-neutral-600 dark:text-neutral-400"
                        >
                          Phone Number *
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98095 48899"
                          className={`w-full h-11 px-4 rounded-xl border bg-transparent text-[#131222] dark:text-white text-sm focus:outline-none focus:ring-2 transition-all ${
                            errors.phone
                              ? "border-red-500 focus:ring-red-500"
                              : "border-neutral-300 dark:border-neutral-700 focus:ring-[#FFB51D] focus:border-transparent"
                          }`}
                        />
                        {errors.phone && (
                          <span className="text-xs text-red-500 mt-1 block font-medium animate-fadeIn">
                            {errors.phone}
                          </span>
                        )}
                      </div>

                      {/* Subject */}
                      <div className="space-y-1.5">
                        <label
                          htmlFor="subject"
                          className="text-xs font-semibold text-neutral-600 dark:text-neutral-400"
                        >
                          Subject *
                        </label>
                        <input
                          id="subject"
                          type="text"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Rental Inquiry"
                          className={`w-full h-11 px-4 rounded-xl border bg-transparent text-[#131222] dark:text-white text-sm focus:outline-none focus:ring-2 transition-all ${
                            errors.subject
                              ? "border-red-500 focus:ring-red-500"
                              : "border-neutral-300 dark:border-neutral-700 focus:ring-[#FFB51D] focus:border-transparent"
                          }`}
                        />
                        {errors.subject && (
                          <span className="text-xs text-red-500 mt-1 block font-medium animate-fadeIn">
                            {errors.subject}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-1.5">
                      <label
                        htmlFor="message"
                        className="text-xs font-semibold text-neutral-600 dark:text-neutral-400"
                      >
                        Message *
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Write your message details here..."
                        className={`w-full p-4 rounded-xl border bg-transparent text-[#131222] dark:text-white text-sm focus:outline-none focus:ring-2 transition-all resize-none ${
                          errors.message
                            ? "border-red-500 focus:ring-red-500"
                            : "border-neutral-300 dark:border-neutral-700 focus:ring-[#FFB51D] focus:border-transparent"
                        }`}
                      />
                      {errors.message && (
                        <span className="text-xs text-red-500 mt-1 block font-medium animate-fadeIn">
                          {errors.message}
                        </span>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-12 bg-gradient-to-r from-[#FFB51D] to-[#FFA500] hover:from-[#FFA500] hover:to-[#FFB51D] text-[#131222] font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          Sending Message... <Loader2 className="w-4 h-4 animate-spin" />
                        </>
                      ) : (
                        <>
                          Send Message <Send className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <IKFooter />
    </SimplePageBackground>
  );
}
